/**
 * Базовый класс FxIdSdk без платформенных зависимостей.
 * Содержит только бизнес-логику и работу с API.
 */
import { fetchProfileData } from "@fxid/openapi-client";
import { SimpleEventDispatcher } from "strongly-typed-events";
import { log } from "./utils/logger";
/**
 * Базовый класс SDK с платформенно-независимой логикой
 * @template TSubscription - Тип подписки, специфичный для платформы
 */
export class FxIdSdkCore {
    constructor(openApiClient, config, environment, uiCallbacks) {
        this.isInitialized = false;
        this.authToken = null;
        // Event dispatchers для двусторонней коммуникации
        this._onChangeAccount = new SimpleEventDispatcher();
        this._onChangeUserInfo = new SimpleEventDispatcher();
        this._onChangeLanguage = new SimpleEventDispatcher();
        this._onAdsFinished = new SimpleEventDispatcher();
        this._onAdsFailed = new SimpleEventDispatcher();
        this._onAdsSkipped = new SimpleEventDispatcher();
        this._onAdsVideoAvailable = new SimpleEventDispatcher();
        this._onAdsInterstitialAvailable = new SimpleEventDispatcher();
        this._onFocusChanged = new SimpleEventDispatcher();
        this._onConsentManagerStatus = new SimpleEventDispatcher();
        this._onCreateSupportRequest = new SimpleEventDispatcher();
        this._onPushToken = new SimpleEventDispatcher();
        this._onPause = new SimpleEventDispatcher();
        this._onResume = new SimpleEventDispatcher();
        this.openApiClient = openApiClient;
        this.config = config;
        this.environment = environment;
        // Merge с заглушками для незаданных callbacks
        this.uiCallbacks = this.mergeUICallbacks(uiCallbacks);
        log.info("[SDK Core] Initialized with config:", {
            game: config.game,
            adapter: config.adapter,
            platform: environment.getPlatformInfo()
        });
    }
    /**
     * Инициализация SDK
     */
    async initialize() {
        if (this.isInitialized) {
            log.warn("[SDK Core] Already initialized");
            return;
        }
        log.info("[SDK Core] Starting initialization");
        // Загружаем токен из хранилища
        this.authToken = this.loadAuthToken();
        if (this.authToken) {
            // Токен установится платформенным слоем при инициализации
            log.info("[SDK Core] Auth token loaded from storage");
        }
        // Проверяем статус согласия
        const consentStatus = this.environment.getConsentStatus();
        if (consentStatus) {
            this.handleConsentStatus(consentStatus);
        }
        // Подписываемся на изменения согласия
        this.environment.onConsentChange((status) => {
            this.handleConsentStatus(status);
        });
        // Инициализируем аналитику
        if (this.environment.isAnalyticsAvailable()) {
            this.initializeAnalytics();
        }
        this.isInitialized = true;
        log.info("[SDK Core] Initialization completed");
    }
    /**
     * Получить хэш из URL
     */
    async getUrlHash() {
        return { hash: this.environment.getLocationHash() };
    }
    /**
     * Установить хэш в URL
     */
    async setUrlHash(hash) {
        if (hash === "") {
            // Очищаем хэш
            const pathname = this.environment.getLocationHref().split('#')[0];
            this.environment.replaceHistoryState(this.environment.getHistoryState(), "", pathname);
        }
        else {
            // Устанавливаем хэш
            this.environment.setLocationHash(hash);
        }
    }
    /**
     * Отправить событие аналитики
     */
    async sendAnalyticsEvent(eventName, eventData) {
        if (!this.environment.isAnalyticsAvailable()) {
            log.warn("[SDK Core] Analytics not available");
            return;
        }
        // Дополняем данные события информацией о пользователе
        const enrichedData = { ...eventData };
        if (!enrichedData.email) {
            try {
                const profile = await fetchProfileData(this.config.game);
                if (profile?.Email) {
                    enrichedData.email = profile.Email;
                }
            }
            catch (err) {
                log.error("[SDK Core] Failed to fetch profile for analytics:", err);
            }
        }
        this.environment.sendAnalyticsEvent(eventName, enrichedData);
        log.info("[SDK Core] Analytics event sent:", eventName, enrichedData);
    }
    /**
     * Аутентификация пользователя
     */
    async authenticateUser(options) {
        log.info("[SDK Core] Authenticating user with options:", options);
        try {
            // Здесь будет логика аутентификации через API
            // Зависит от конкретной платформы
            // После успешной аутентификации сохраняем токен
            const token = await this.performAuthentication(options);
            if (token) {
                this.saveAuthToken(token);
                // Платформенный слой установит токен при следующей загрузке
                this.authToken = token;
                // Перезагружаем приложение для применения токена
                this.environment.reload();
            }
        }
        catch (error) {
            log.error("[SDK Core] Authentication failed:", error);
            throw error;
        }
    }
    /**
     * Показать модальное окно логина
     */
    async showLoginModal() {
        this.uiCallbacks.openLoginModal({
            onAuthenticated: () => {
                log.info("[SDK Core] User authenticated via modal");
                this.environment.reload();
            }
        });
    }
    /**
     * Получить информацию о платформе
     */
    getPlatformInfo() {
        return this.environment.getPlatformInfo();
    }
    /**
     * Проверить, инициализирован ли SDK
     */
    getIsInitialized() {
        return this.isInitialized;
    }
    /**
     * Событие смены аккаунта
     */
    get OnChangeAccount() {
        return this._onChangeAccount.asEvent();
    }
    /**
     * Событие изменения информации о пользователе
     */
    get OnChangeUserInfo() {
        return this._onChangeUserInfo.asEvent();
    }
    /**
     * Событие смены языка
     */
    get OnChangeLanguage() {
        return this._onChangeLanguage.asEvent();
    }
    /**
     * Событие завершения рекламы
     */
    get OnAdsFinished() {
        return this._onAdsFinished.asEvent();
    }
    /**
     * Событие ошибки рекламы
     */
    get OnAdsFailed() {
        return this._onAdsFailed.asEvent();
    }
    /**
     * Событие пропуска рекламы
     */
    get OnAdsSkipped() {
        return this._onAdsSkipped.asEvent();
    }
    /**
     * Событие доступности видеорекламы
     */
    get OnAdsVideoAvailable() {
        return this._onAdsVideoAvailable.asEvent();
    }
    /**
     * Событие доступности межстраничной рекламы
     */
    get OnAdsInterstitialAvailable() {
        return this._onAdsInterstitialAvailable.asEvent();
    }
    /**
     * Событие изменения фокуса
     */
    get OnFocusChanged() {
        return this._onFocusChanged.asEvent();
    }
    /**
     * Событие статуса Consent Manager
     */
    get OnConsentManagerStatus() {
        return this._onConsentManagerStatus.asEvent();
    }
    /**
     * Событие создания запроса в поддержку
     */
    get OnCreateSupportRequest() {
        return this._onCreateSupportRequest.asEvent();
    }
    /**
     * Событие получения push токена
     */
    get OnPushToken() {
        return this._onPushToken.asEvent();
    }
    /**
     * Событие паузы
     */
    get OnPause() {
        return this._onPause.asEvent();
    }
    /**
     * Событие возобновления
     */
    get OnResume() {
        return this._onResume.asEvent();
    }
    // ========== Приватные методы ==========
    /**
     * Выполнить аутентификацию (должен быть переопределен в наследниках)
     */
    async performAuthentication(options) {
        log.warn("[SDK Core] performAuthentication not implemented, override in platform-specific class");
        return null;
    }
    /**
     * Загрузить токен аутентификации из хранилища
     */
    loadAuthToken() {
        const token = this.environment.getStorageItem('fxid_auth_token');
        return token;
    }
    /**
     * Сохранить токен аутентификации в хранилище
     */
    saveAuthToken(token) {
        this.environment.setStorageItem('fxid_auth_token', token);
    }
    /**
     * Обработать статус согласия
     */
    handleConsentStatus(status) {
        log.info("[SDK Core] Consent status updated:", status);
        // Включаем/отключаем аналитику в зависимости от согласия
        if (status.analytics === false || status.googleAds === false) {
            log.info("[SDK Core] User denied analytics consent");
            // Можем отключить отправку событий
        }
    }
    /**
     * Инициализировать аналитику
     */
    initializeAnalytics() {
        log.info("[SDK Core] Initializing analytics");
        // Отправляем событие инициализации
        this.environment.sendAnalyticsEvent('sdk_initialized', {
            game: this.config.game,
            adapter: this.config.adapter,
            platform: this.environment.getPlatformInfo().type
        });
    }
    /**
     * Объединить UI callbacks с заглушками
     */
    mergeUICallbacks(callbacks) {
        const stubs = {
            openTextCopyModal: (params) => {
                log.warn("[SDK Core] openTextCopyModal not implemented", params);
            },
            openLoginModal: (params) => {
                log.warn("[SDK Core] openLoginModal not implemented", params);
            },
            openReportIssueModal: (params) => {
                log.warn("[SDK Core] openReportIssueModal not implemented", params);
            },
            openPushPermissionModal: async (params) => {
                log.warn("[SDK Core] openPushPermissionModal not implemented", params);
            },
            openLinkShareModal: (params) => {
                log.warn("[SDK Core] openLinkShareModal not implemented", params);
            }
        };
        return { ...stubs, ...callbacks };
    }
    /**
     * Обработать события safe area (для мобильных платформ)
     */
    async getSafeAreaInsets() {
        // Получаем значения safe area из CSS переменных
        const top = this.parseCSSValue(this.environment.getCSSVariable("--sat"));
        const bottom = this.parseCSSValue(this.environment.getCSSVariable("--sab"));
        const left = this.parseCSSValue(this.environment.getCSSVariable("--sal"));
        const right = this.parseCSSValue(this.environment.getCSSVariable("--sar"));
        return { top, bottom, left, right };
    }
    /**
     * Парсить CSS значение в число
     */
    parseCSSValue(value) {
        if (!value)
            return 0;
        const match = value.match(/(\d+(?:\.\d+)?)/);
        return match ? parseFloat(match[1]) : 0;
    }
    // ========== Public Dispatch Methods ==========
    /**
     * Диспатчит событие доступности видеорекламы
     */
    dispatchAdsVideoAvailable() {
        this._onAdsVideoAvailable.dispatch();
    }
    /**
     * Диспатчит событие доступности межстраничной рекламы
     */
    dispatchAdsInterstitialAvailable() {
        this._onAdsInterstitialAvailable.dispatch();
    }
    /**
     * Диспатчит событие завершения рекламы
     */
    dispatchAdsFinished() {
        this._onAdsFinished.dispatch();
    }
    /**
     * Диспатчит событие ошибки рекламы
     */
    dispatchAdsFailed() {
        this._onAdsFailed.dispatch();
    }
    /**
     * Диспатчит событие пропуска рекламы
     */
    dispatchAdsSkipped() {
        this._onAdsSkipped.dispatch();
    }
    /**
     * Диспатчит событие изменения фокуса
     */
    dispatchFocusChanged(data) {
        this._onFocusChanged.dispatch(data);
    }
    /**
     * Диспатчит событие паузы
     */
    dispatchPause() {
        this._onPause.dispatch();
    }
    /**
     * Диспатчит событие возобновления
     */
    dispatchResume() {
        this._onResume.dispatch();
    }
    /**
     * Диспатчит событие статуса Consent Manager
     */
    dispatchConsentManagerStatus(status) {
        this._onConsentManagerStatus.dispatch(status);
    }
    /**
     * Диспатчит событие изменения информации о пользователе
     */
    dispatchOnChangedUserInfo(changeInfoEvent) {
        log.info("[SDK Core] Dispatching onChangeUserInfo event: %o", changeInfoEvent);
        this._onChangeUserInfo.dispatch(changeInfoEvent);
    }
    // ========== Status and Misc API Methods ==========
    /**
     * Установить статус пользователя
     */
    async setStatusApi(requestJson) {
        const request = JSON.parse(requestJson);
        await this.openApiClient.Friends.fxIdWebFeaturesFriendsSetStatusEndpoint({
            Name: request.name,
            Priority: request.priority,
            Status: request.status
        });
    }
    // ========== Documents API Methods ==========
    /**
     * Получить принятые документы аккаунта
     */
    async getAccountAcceptedDocumentsApi() {
        const response = await this.openApiClient.Games.fxIdWebFeaturesDocumentsGetAcceptedDocumentsGetAcceptedDocumentsEndpoint();
        return response.GameDocumentAccountAccepts.map((accept) => this.mapDocumentAccept(accept));
    }
    /**
     * Принять документ
     */
    async accountAcceptDocumentApi(requestJson) {
        const request = JSON.parse(requestJson);
        await this.openApiClient.Games.fxIdWebFeaturesDocumentsAcceptDocumentAcceptDocumentEndpoint(request);
    }
    /**
     * Получить игровые документы
     */
    async getGameDocumentsApi(requestJson) {
        const request = JSON.parse(requestJson);
        const response = await this.openApiClient.Games.fxIdWebFeaturesDocumentsGetAllDocumentsWithVersionsGetAllDocumentsWithVersionsEndpoint(request);
        return response.Documents.map((doc) => ({
            Id: doc.Id,
            Type: doc.DocumentType,
            CreatedAt: doc.CreatedAt,
            IsActive: doc.IsActive,
            GameSystemName: doc.GameSystemName,
            Versions: doc.Versions.map((version) => this.mapDocumentVersion(version))
        }));
    }
    /**
     * Маппит версию документа из формата API в DTO для SDK
     */
    mapDocumentVersion(version) {
        return {
            Id: version.Id,
            DocumentId: version.DocumentId,
            Url: version.Url,
            CreatedAt: version.CreatedAt
        };
    }
    /**
     * Маппит принятый пользователем документ из формата API в DTO для SDK
     */
    mapDocumentAccept(accept) {
        return {
            Id: accept.Id,
            DocumentVersionId: accept.DocumentVersionId,
            AccountId: accept.AccountId,
            AcceptedAt: accept.AcceptedAt
        };
    }
}
